// src/hooks/useMessageNotifications.js
import { useEffect, useState, useCallback, useMemo } from "react";
import { supabase } from "@/lib/supabaseClient";
import { toast } from "react-toastify";

/**
 * Hook pour gérer les notifications de messages en temps réel
 *
 * @param {string} userId - ID de l'utilisateur connecté
 * @returns {Object} { unreadByConversation, totalUnread, markConversationAsRead, refreshUnreadCount }
 */
export function useMessageNotifications(userId) {
  const [unreadByConversation, setUnreadByConversation] = useState({});
  const [peopleById, setPeopleById] = useState({});

  // Charger le nombre de messages non lus pour toutes les conversations
  const loadUnreadCounts = useCallback(async () => {
    if (!userId) return;

    try {
      const { data, error } = await supabase.rpc("get_conversations_with_unread_count");
      if (error) throw error;

      const counts = {};
      if (Array.isArray(data)) {
        data.forEach((row) => {
          counts[row.conversation_id] = row.unread_count || 0;
        });
      }
      setUnreadByConversation(counts);
    } catch (err) {
      console.error("[useMessageNotifications] loadUnreadCounts error:", err);
    }
  }, [userId]);

  // Charger les infos des utilisateurs pour afficher leurs noms dans les toasts
  const loadPeopleInfo = useCallback(async (userIds) => {
    if (!userIds || userIds.length === 0) return;

    try {
      const { data, error } = await supabase.rpc("get_profile_basics", {
        p_user_ids: userIds,
      });
      if (error) throw error;

      if (Array.isArray(data)) {
        setPeopleById((prev) => {
          const next = { ...prev };
          data.forEach((p) => {
            next[p.id] = p;
          });
          return next;
        });
      }
    } catch (err) {
      console.error("[useMessageNotifications] loadPeopleInfo error:", err);
    }
  }, []);

  // Marquer une conversation comme lue
  const markConversationAsRead = useCallback(
    async (conversationId) => {
      if (!userId || !conversationId) return;

      try {
        const { error } = await supabase.rpc("mark_conversation_as_read", {
          p_conversation_id: conversationId,
        });
        if (error) throw error;

        setUnreadByConversation((prev) => ({
          ...prev,
          [conversationId]: 0,
        }));
      } catch (err) {
        console.error("[useMessageNotifications] markConversationAsRead error:", err);
      }
    },
    [userId]
  );

  // Rafraîchir le compteur
  const refreshUnreadCount = useCallback(() => {
    loadUnreadCounts();
  }, [loadUnreadCounts]);

  // Total non-lus
  const totalUnread = useMemo(() => {
    return Object.values(unreadByConversation).reduce((sum, count) => sum + (count || 0), 0);
  }, [unreadByConversation]);

  // Realtime
  useEffect(() => {
    if (!userId) return;

    loadUnreadCounts();

    const channel = supabase
      .channel("new_messages_notification")
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "messages", // ✅ ICI: c’était coupé chez toi ("me...)
        },
        async (payload) => {
          const newMessage = payload.new;
          if (!newMessage) return;

          // Ignorer ses propres messages
          if (newMessage.sender_id === userId) return;

          // Charger la conversation pour vérifier participant
          const { data: conv, error: convErr } = await supabase
            .from("conversations")
            .select("id, expert_id, client_id, subject")
            .eq("id", newMessage.conversation_id)
            .maybeSingle();

          if (convErr || !conv) return;

          const isParticipant = conv.expert_id === userId || conv.client_id === userId;
          if (!isParticipant) return;

          // Incrément local
          setUnreadByConversation((prev) => ({
            ...prev,
            [newMessage.conversation_id]: (prev[newMessage.conversation_id] || 0) + 1,
          }));

          // Info expéditeur
          if (!peopleById[newMessage.sender_id]) {
            await loadPeopleInfo([newMessage.sender_id]);
          }

          const sender = peopleById[newMessage.sender_id];
          const senderName = sender
            ? `${sender.first_name || ""} ${sender.last_name || ""}`.trim() || "Utilisateur"
            : "Utilisateur";

          const subject = conv.subject || "Conversation";

          toast.info(
            <div>
              <strong>🔔 Nouveau message de {senderName}</strong>
              <div style={{ fontSize: 12, marginTop: 4, opacity: 0.8 }}>{subject}</div>
            </div>,
            {
              position: "top-right",
              autoClose: 5000,
              hideProgressBar: false,
              closeOnClick: true,
              pauseOnHover: true,
              draggable: true,
            }
          );
        }
      )
      .subscribe();

    return () => {
      try {
        supabase.removeChannel(channel);
      } catch (_) {}
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, loadUnreadCounts, loadPeopleInfo, peopleById]);

  return {
    unreadByConversation,
    totalUnread,
    markConversationAsRead,
    refreshUnreadCount,
  };
}